import { useState, useMemo } from 'react';
import { PrimesData, StatusType } from '../types';
import { 
  calculatePSSAmount, 
  calculateISSAmount, 
  calculateIRAmount,
  calculatePrimeNuitAmount,
  calculateSFTAmount,
  calculateCTIAmount,
  calculateTotalMonthlyPrimes,
  filterPrimesForProfession,
  calculateAdjustedPrimeAmount,
  calculatePercentageOfTreatment,
  calculateInsalubriteCat2Amount,
  calculateNBIAmount,
  filterPrimesByStatus,
  filterPrimesByEchelon
} from '../utils/primeCalculations';
import { PRIME_CALCULATION } from '../constants/primes';
import { GRADES_IDE } from '../constants/salary';
import { DEFAULT_PRIMES } from '../constants/primes';

interface UsePrimeCalculationsProps {
  selectedGrade: number | null;
  selectedEchelon: number | null;
  selectedZoneIR: number | null;
  primesData: PrimesData;
  currentProfessionId: string;
  selectedStatus: StatusType;
  isLoading: boolean;
}

interface PrimeCalculationAmounts {
  pss: number;
  iss: number;
  ir: number;
  primeNuit: number;
  sft: number;
  insalubriteCat2: number;
  nbi: number;
}

export function usePrimeCalculations({
  selectedGrade,
  selectedEchelon,
  selectedZoneIR,
  primesData,
  currentProfessionId,
  selectedStatus,
  isLoading
}: UsePrimeCalculationsProps) {
  const [isCalculatingPrimes, setIsCalculatingPrimes] = useState(false);
  
  // Calculate current indice majoré
  const currentIndiceMajore = useMemo(() => {
    if (!selectedGrade || selectedEchelon === null) return null;
    
    const currentEchelon = GRADES_IDE
      .find(grade => grade.id === selectedGrade)
      ?.echelons.find(e => e.echelon === selectedEchelon);
    
    return currentEchelon?.indiceMajoré || null;
  }, [selectedGrade, selectedEchelon]);

  // Filter available primes for current profession
  const availablePrimes = useMemo(() => {
    const source = (isLoading || primesData.primes.length === 0) ? DEFAULT_PRIMES : primesData.primes;
    const professionFiltered = filterPrimesForProfession(source, currentProfessionId);
    const statusFiltered = filterPrimesByStatus(professionFiltered, selectedStatus);
    return filterPrimesByEchelon(statusFiltered, selectedEchelon);
  }, [primesData.primes, currentProfessionId, selectedStatus, selectedEchelon, isLoading]);

  // Calculate automatic amounts
  const calculatedAmounts = useMemo((): PrimeCalculationAmounts => {
    if (isLoading || !currentIndiceMajore || currentIndiceMajore <= 0) {
      return { pss: 0, iss: 0, ir: 0, primeNuit: 0, sft: 0, insalubriteCat2: 0, nbi: 0 };
    }

    // Simulate calculation delay
    setIsCalculatingPrimes(true);
    const timer = setTimeout(() => setIsCalculatingPrimes(false), 300);

    const pss = calculatePSSAmount(currentIndiceMajore);
    
    // Calculate ISS using historical echelon if configured
    const issPrime = availablePrimes.find(p => p.id === 'iss');
    const iss = issPrime?.issConfig?.indiceMajoreHistorique && issPrime.issConfig.indiceMajoreHistorique > 0
      ? calculateISSAmount(issPrime.issConfig.indiceMajoreHistorique)
      : 0; // Default to 0 if no historical echelon selected
    
    const ir = selectedZoneIR !== null 
      ? calculateIRAmount(currentIndiceMajore, selectedZoneIR as 1 | 2 | 3)
      : 0;
    
    // Calculate prime de nuit
    const primeNuitPrime = availablePrimes.find(p => p.id === 'prime_nuit');
    const nuitsParMois = primeNuitPrime?.primeNuitConfig?.nuitsParMois || 0;
    const primeNuit = calculatePrimeNuitAmount(currentIndiceMajore, nuitsParMois);

    // Calculate SFT
    const sftPrime = availablePrimes.find(p => p.id === 'sft');
    const nombreEnfants = sftPrime?.sftConfig?.nombreEnfants || 0;
    const sft = Math.round(calculateSFTAmount(currentIndiceMajore, nombreEnfants));

    // Calculate insalubrité cat 2
    const insalubriteCat2Prime = availablePrimes.find(p => p.id === 'insalubrite');
    const montantDemiJournee = insalubriteCat2Prime?.montantDemiJourneeInsalubrite || 0.31;
    const joursTravaillesParMois = insalubriteCat2Prime?.insalubriteCat2Config?.joursTravaillesParMois || 22;
    const insalubriteCat2 = calculateInsalubriteCat2Amount(montantDemiJournee, joursTravaillesParMois);

    // Calculate NBI
    const nbiPrime = availablePrimes.find(p => p.id === 'nbi');
    const nbiPoints = nbiPrime?.nbiConfig?.points || 15;
    const nbi = calculateNBIAmount(nbiPoints);

    return { pss, iss, ir, primeNuit, sft, insalubriteCat2, nbi };
  }, [currentIndiceMajore, selectedZoneIR, availablePrimes, isLoading]);

  // Create primes with calculated amounts
  const primesWithCalculatedAmounts = useMemo(() => {
    if (isLoading) return [];
    
    return availablePrimes.map(prime => {
      switch (prime.id) {
        case 'segur':
          // Calculate CTI Ségur using CTI points
          const ctiAmount = prime.ctiPoints ? calculateCTIAmount(prime.ctiPoints) : 0;
          return { ...prime, montantUtilisateur: ctiAmount };
        case 'pss':
          return { ...prime, montantUtilisateur: calculatedAmounts.pss };
        case 'iss':
          return { ...prime, montantUtilisateur: calculatedAmounts.iss };
        case 'indemnite_residence':
          return { ...prime, montantUtilisateur: calculatedAmounts.ir };
        case 'prime_nuit':
          return { ...prime, montantUtilisateur: calculatedAmounts.primeNuit };
        case 'sft':
          return { ...prime, montantUtilisateur: calculatedAmounts.sft };
        case 'insalubrite':
          return { ...prime, montantUtilisateur: calculatedAmounts.insalubriteCat2 };
        case 'nbi':
          return { ...prime, montantUtilisateur: calculatedAmounts.nbi };
        case 'service':
          // Calculate maximum amount for service prime (17% of annual treatment)
          if (prime.isMaxCalculated && prime.serviceTauxMax && currentIndiceMajore) {
            const maxAmount = calculatePercentageOfTreatment(currentIndiceMajore, prime.serviceTauxMax);
            const updatedPrime = { 
              ...prime, 
              montantDefaut: maxAmount 
            };
            // If user amount is 0 (initial) or exceeds max, cap it to max
            if (prime.montantUtilisateur === 0 || prime.montantUtilisateur > maxAmount) {
              updatedPrime.montantUtilisateur = maxAmount;
            }
            return updatedPrime;
          }
          return prime;
        default:
          return prime;
      }
    });
  }, [availablePrimes, calculatedAmounts, isLoading]);

  // Calculate display amounts for max values
  const displayAmounts = useMemo(() => {
    const maxIRAmount = currentIndiceMajore ? Math.round(calculateIRAmount(currentIndiceMajore, 1) * 100) / 100 : 0;
    
    // Calculate max IFTD amount for display
    const maxDimanches = 4;
    const maxJoursFeries = 11;
    const montantDimanches = 60 * maxDimanches;
    const montantJoursFeries = (60 * maxJoursFeries) / 12;
    const maxIFTDAmount = Math.round((montantDimanches + montantJoursFeries) * 100) / 100;
    
    // Calculate max Prime de nuit amount for display (31 nuits maximum)
    const maxPrimeNuitAmount = currentIndiceMajore ? Math.round(calculatePrimeNuitAmount(currentIndiceMajore, 31) * 100) / 100 : 0;
    
    return { 
      maxIRAmount, 
      maxIFTDAmount, 
      maxPrimeNuitAmount,
      maxServiceAmount: PRIME_CALCULATION.SERVICE_MAX_AMOUNT
    };
  }, [currentIndiceMajore]);

  // Calculate total monthly primes
  const totalMonthlyPrimes = useMemo(() => {
    return calculateTotalMonthlyPrimes(primesWithCalculatedAmounts, 100);
  }, [primesWithCalculatedAmounts]);

  return {
    currentIndiceMajore,
    calculatedAmounts,
    primesWithCalculatedAmounts,
    availablePrimes,
    isCalculatingPrimes,
    displayAmounts,
    totalMonthlyPrimes
  };
}